import React, { useCallback, useEffect, useRef, useState } from "react";
import "./ScrollTemp.css";
import { FiPlus, FiArrowRight } from "react-icons/fi";
import TemplateSkeleton from "../../Components/SkeletonLoader/TemplateSkeleton";
import { TEMPLATE_CATALOG } from "./templateCatalog";
import TemplateHoverPreview from "./TemplateHoverPreview";

export const TEMPLATE_HOVER_DELAY_MS = 200;

const supportsHoverPreview = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(hover: hover) and (pointer: fine)").matches;

export default function ScrollTemp({
  templatesList,
  template,
  setTemplate,
  setTemplateId,
  setNewProjectInputModalOpen,
  onCustomProjectClick,
  onTemplateClick,
  allTemplateLoading,
}) {
  const templateSkeletonCount = 6;
  const hoverTimerRef = useRef(null);
  const [hoverPreview, setHoverPreview] = useState(null);

  const hideHoverPreview = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    setHoverPreview(null);
  }, []);

  const scheduleHoverPreview = useCallback((item, element) => {
    if (!supportsHoverPreview()) return;

    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    const anchorRect = element.getBoundingClientRect();
    hoverTimerRef.current = setTimeout(() => {
      setHoverPreview({ item, anchorRect });
      hoverTimerRef.current = null;
    }, TEMPLATE_HOVER_DELAY_MS);
  }, []);

  useEffect(() => hideHoverPreview, [hideHoverPreview]);

  useEffect(() => {
    if (!hoverPreview) return undefined;

    window.addEventListener("resize", hideHoverPreview);
    window.addEventListener("scroll", hideHoverPreview, true);
    return () => {
      window.removeEventListener("resize", hideHoverPreview);
      window.removeEventListener("scroll", hideHoverPreview, true);
    };
  }, [hoverPreview, hideHoverPreview]);

  const allTemplateSkelton = () =>
    Array.from({ length: templateSkeletonCount }, (_, i) => (
      <TemplateSkeleton key={`template-skeleton-${i}`} />
    ));

  const list =
    templatesList?.length > 0 ? templatesList : TEMPLATE_CATALOG;

  return (
    <div className="w-full flex items-stretch gap-4">
      {/* Custom Project — fixed in place, always the first tile */}
      <div
        className="tplCard tplCardCustom flex-shrink-0"
        onClick={onCustomProjectClick}
      >
        <div className="tplIconCircle">
          <FiPlus className="w-7 h-7" />
        </div>
        <span className="tplTitle tplTitleCustom font-body">
          Custom Project
        </span>
      </div>

      {/* Templates — horizontally scrollable */}
      <div className="tplScrollRow flex-1 min-w-0">
        {allTemplateLoading
          ? allTemplateSkelton()
          : list?.map((item, idx) => (
              <div
                className="tplCard"
                key={`${item?.label}-${item?.templateId ?? idx}`}
                data-testid={`template-card-${item?.templateId ?? item?.id ?? idx}`}
                onMouseEnter={(event) =>
                  scheduleHoverPreview(item, event.currentTarget)
                }
                onMouseLeave={hideHoverPreview}
                onClick={() => {
                  hideHoverPreview();
                  setTemplateId(item?.templateId ?? item?.id);
                  if (setTemplate) setTemplate(false);
                  onTemplateClick?.(item);
                }}
              >
                <span className="tplBadge">
                  {item?.type || item?.badge || "SCROLL"}
                </span>
                <div className="tplCardBody">
                  <span className="tplTitle font-body">
                    {item?.label}
                  </span>
                  <p className="tplDesc">
                    {item?.shortDescription || item?.description || ""}
                  </p>
                </div>
                <div className="tplPreviewCue" aria-hidden="true">
                  Preview <FiArrowRight className="tplPreviewCue__icon" />
                </div>
              </div>
            ))}
      </div>
      {hoverPreview && (
        <TemplateHoverPreview
          template={hoverPreview.item}
          anchorRect={hoverPreview.anchorRect}
        />
      )}
    </div>
  );
}
